import { DateTimeRangePicker, DropDownList, FiltersV2, Loading, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import type { PreEnrollmentValidationFilter } from "./types";

import PreEnrollmentValidationApi from "../../api/preEnrollmentValidationApi";
import { SegmentFilters } from "../../components/segmentFilters";
import { PreEnrollmentValidationGrid } from "./grid";
import * as STRINGS from "./strings";
import style from "./style.module.scss";

const initialFilters: PreEnrollmentValidationFilter = {
	currentPage: 1,
	itemsPerPage: 20,
	startDate: null,
	endDate: null,
	validationTypeIds: [],
	branchIds: [],
};

export function PreEnrollmentValidationPage() {
	const [draftFilters, setDraftFilters] = useState<PreEnrollmentValidationFilter>(initialFilters);
	const [appliedFilters, setAppliedFilters] = useState<PreEnrollmentValidationFilter>(initialFilters);

	const validationTypesQuery = useQuery({
		queryKey: ["EnrollmentValidationTypes"],
		queryFn: () => PreEnrollmentValidationApi.getTypes(),
	});

	const validationTypeOptions = useMemo(
		() => validationTypesQuery.data?.map((t) => ({ label: t.Name, value: String(t.Id) })) ?? [],
		[validationTypesQuery.data],
	);

	const filterChanged = (name: string, value: any) => {
		setDraftFilters((prev) => ({ ...prev, [name]: value }));
	};

	const segmentFiltersChanged = useCallback(
		(s) =>
			setDraftFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setDraftFilters],
	);

	return (
		<Loading loading={false}>
			<main>
				<FiltersV2
					onApply={() =>
						setAppliedFilters((currentFilters) => ({
							...draftFilters,
							currentPage: 1,
							itemsPerPage: currentFilters.itemsPerPage,
						}))
					}
				>
					<DateTimeRangePicker
						dateOnly
						id="date-range"
						label="Date of Error Range"
						className={style.dateTimeRangePicker}
						quickRange
						quickRangeOptions={["today", "yesterday", "quarter", "year", "all"]}
						value={[draftFilters.startDate, draftFilters.endDate]}
						setValue={(v: (Date | undefined)[]) =>
							setDraftFilters({ ...draftFilters, startDate: v[0], endDate: v[1] })
						}
						onQuickRangeSelected={(v) =>
							setAppliedFilters((currentFilters) => ({
								...draftFilters,
								startDate: v[0],
								endDate: v[1],
								currentPage: 1,
								itemsPerPage: currentFilters.itemsPerPage,
							}))
						}
					/>

					<DropDownList
						id={STRINGS.VALIDATION_TYPE_IDS}
						label="Validation Type"
						value={draftFilters.validationTypeIds?.[0]?.toString() || ""}
						changeHandler={(v) => filterChanged(STRINGS.VALIDATION_TYPE_IDS, v ? [parseInt(v)] : [])}
						fullLength
						clearable
						loaded={validationTypesQuery.isFetched}
						values={validationTypeOptions}
					/>

					<SegmentFilters onChange={segmentFiltersChanged} />
					<TextBox
						id={STRINGS.AGENT_ID}
						label="Agent Id"
						value={draftFilters.agentId ?? ""}
						changeHandler={filterChanged}
					/>
					<div className="flex gap-1 [&>div]:m-0">
						<TextBox
							id={STRINGS.ENROLLMENT_ID}
							label="Enrollment Id"
							value={draftFilters.enrollmentId ?? ""}
							changeHandler={filterChanged}
						/>
						<TextBox
							id={STRINGS.SALE_ID}
							label="Sale Id"
							value={draftFilters.saleId ?? ""}
							changeHandler={filterChanged}
						/>
					</div>
					<TextBox
						id={STRINGS.ENTITY_VALUE}
						label="Entity Value"
						value={draftFilters.entityValue ?? ""}
						changeHandler={filterChanged}
					/>
				</FiltersV2>
				<PreEnrollmentValidationGrid filters={appliedFilters} setFilters={setAppliedFilters} isAdmin={true} />
			</main>
		</Loading>
	);
}
